import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Repeat, Building2, Search, Loader2, RefreshCw, Send, Trash2, Plus,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchFromCCList, fetchRecievedCCList, fetchCategories,
    fetchAutoCompleteItem, fetchTempDetails, saveTempItems,
    fetchCartGrid, deleteCartItem, saveTransfer,
    clearSuggestions, clearTempItems, clearSaveTempResult, clearSubmitResult, resetNewStockTransfer,
    selectNSTFromCCList, selectNSTRecCCList, selectNSTCategoryList,
    selectNSTSuggestions, selectNSTSuggestionsLoading,
    selectNSTTempItems, selectNSTTempLoading,
    selectNSTCartItems, selectNSTCartLoading,
    selectNSTLoading, selectNSTSubmitResult,
} from '../../slices/purchaseSlice/newStockTransferSlice';

// ── Shared UI (mirrors NewStockReceived / DailyIssue) ───────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const todayFormatted = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};
const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
    return String(d);
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const SELECTION_TYPES = [
    { id: '1', label: 'Search By Item Code' },
    { id: '2', label: 'Search By Item Name' },
];
const DAYS_OPTIONS = ['1', '2', '3', '4', '5', '6', '7'];

const NewStockTransfer = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const fromCCList = useSelector(selectNSTFromCCList);
    const recCCList = useSelector(selectNSTRecCCList);
    const categoryList = useSelector(selectNSTCategoryList);
    const suggestions = useSelector(selectNSTSuggestions);
    const suggestionsLoading = useSelector(selectNSTSuggestionsLoading);
    const tempItems = useSelector(selectNSTTempItems);
    const tempLoading = useSelector(selectNSTTempLoading);
    const cartItems = useSelector(selectNSTCartItems);
    const cartLoading = useSelector(selectNSTCartLoading);
    const loading = useSelector(selectNSTLoading);
    const submitResult = useSelector(selectNSTSubmitResult);

    const [fromCC, setFromCC] = useState('');
    const [toCC, setToCC] = useState('');
    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchedItemCode, setSearchedItemCode] = useState('');

    const [tempQtyEdits, setTempQtyEdits] = useState({});
    const [cartRemarks, setCartRemarks] = useState({});
    const [cartChecked, setCartChecked] = useState({});

    const [transferDate, setTransferDate] = useState(todayFormatted());
    const [days, setDays] = useState('');
    const [commonRemarks, setCommonRemarks] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const debounceRef = useRef(null);

    useEffect(() => {
        if (uid && roleId) {
            dispatch(fetchFromCCList({ uid, rid: roleId }));
            dispatch(fetchRecievedCCList({ uid, rid: roleId }));
            dispatch(fetchCategories());
        }
        return () => dispatch(resetNewStockTransfer());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    // Mirrors legacy's GetgridDetails(): changing the From CC clears the search/temp state and
    // reloads the staged cart for that CC.
    const handleFromCCChange = (val) => {
        setFromCC(val);
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearSuggestions());
        dispatch(clearTempItems());
        setCartRemarks({}); setCartChecked({});
        if (val) dispatch(fetchCartGrid(val));
    };

    const handleCategoryChange = (val) => {
        setCategory(val);
        setSelectionType('');
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSelectionTypeChange = (val) => {
        if (!category) { toast.warning('Select Search By Category'); return; }
        setSelectionType(val);
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSearchChange = (text) => {
        const upper = text.toUpperCase();
        setSearchText(upper);
        clearTimeout(debounceRef.current);
        if (!fromCC) { toast.warning('Select Transfer From Cost Centre'); return; }
        if (!category) { toast.warning('Select Search By Category'); return; }
        if (!selectionType) { toast.warning('Select Search By Type'); return; }
        if (!upper.trim()) { dispatch(clearSuggestions()); setShowSuggestions(false); return; }
        debounceRef.current = setTimeout(() => {
            dispatch(fetchAutoCompleteItem({ prefix: upper.trim(), type: selectionType, cat: category, cc: fromCC }));
            setShowSuggestions(true);
        }, 300);
    };

    const selectSuggestion = (label) => {
        setSearchText(label);
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearTempItems());
    };

    // SearchItem label is "ItemCode , ItemName,Units   ||    Quantity"
    const handleAddItem = () => {
        if (!category) { toast.warning('Select Search By Category'); return; }
        if (!selectionType) { toast.warning('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.warning('Enter Search Data'); return; }
        if (!fromCC) { toast.warning('Select Transfer From Cost Centre'); return; }

        const itemCode = searchText.split(',')[0].trim();
        setSearchedItemCode(itemCode);
        dispatch(fetchTempDetails({ itemcode: itemCode, cccode: fromCC }));
    };

    useEffect(() => {
        if (tempItems.length > 0) {
            const init = {};
            tempItems.forEach((r) => { init[r.TempId] = '0.00'; });
            setTempQtyEdits(init);
        }
    }, [tempItems]);

    const tempTotals = useMemo(() => {
        const assigned = tempItems.reduce((sum, r) => sum + num(tempQtyEdits[r.TempId]), 0);
        const accQty = num(tempItems[0]?.ACCQty);
        return { assigned, accQty };
    }, [tempItems, tempQtyEdits]);

    const handleTempQtyChange = (row, value) => {
        const avlQty = num(row.TempQuantity);
        const accQty = num(row.ACCQty);
        if (value !== '' && num(value) > avlQty) {
            toast.error('Invalid Transfer Quantity');
            setTempQtyEdits((p) => ({ ...p, [row.TempId]: '0' }));
            return;
        }
        if (value !== '' && num(value) > accQty) {
            toast.error('Invalid Transfer Quantity');
            setTempQtyEdits((p) => ({ ...p, [row.TempId]: '0' }));
            return;
        }
        setTempQtyEdits((p) => ({ ...p, [row.TempId]: value }));
    };

    const handleAddToTransfer = async () => {
        if (!fromCC) { toast.warning('Select Transfer From Cost Centre'); return; }
        const qualifying = tempItems.filter((r) => num(tempQtyEdits[r.TempId]) > 0);
        if (qualifying.length === 0) { toast.error('Invalid Submission'); return; }
        if (tempTotals.assigned > tempTotals.accQty) { toast.error('Invalid Quantity'); return; }

        const payload = {
            Trowids: qualifying.map((r) => r.TempId).join(',') + ',',
            TItemCodes: qualifying.map((r) => r.TempItemcode).join(',') + ',',
            TNewQtys: qualifying.map((r) => num(tempQtyEdits[r.TempId])).join(',') + ',',
            Ttblnames: qualifying.map((r) => r.Tblname).join(',') + ',',
            TCCCode: fromCC,
            TSingleItemcode: searchedItemCode,
        };

        try {
            const result = await dispatch(saveTempItems(payload)).unwrap();
            const [status, code] = (result || '').split(',');
            if (status === 'Submited') {
                toast.success(`Item Code '${code}' Added Successfully`);
                setSearchText('');
                dispatch(clearTempItems());
                dispatch(fetchCartGrid(fromCC));
            } else {
                toast.error(result || 'Failed to add item');
            }
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Adding Items');
        } finally {
            dispatch(clearSaveTempResult());
        }
    };

    const handleCartCheck = (itemcode, checked) => setCartChecked((p) => ({ ...p, [itemcode]: checked }));
    const handleCartRemark = (itemcode, value) => setCartRemarks((p) => ({ ...p, [itemcode]: value }));

    const handleDeleteCartItem = async (row) => {
        if (!cartChecked[row.OItemcode]) { toast.error('Please Check for Delete'); return; }
        try {
            await dispatch(deleteCartItem({ DItemcode: row.OItemcode, DCCCode: row.OCCCode })).unwrap();
            toast.success('Deleted Successfully');
            dispatch(fetchCartGrid(fromCC));
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Deleting Item');
        }
    };

    const cartTotal = useMemo(() => num(cartItems[0]?.OSumTotal), [cartItems]);

    const handleReset = () => {
        dispatch(resetNewStockTransfer());
        setFromCC(''); setToCC(''); setCategory(''); setSelectionType(''); setSearchText('');
        setTempQtyEdits({}); setCartRemarks({}); setCartChecked({});
        setTransferDate(todayFormatted()); setDays(''); setCommonRemarks(''); setSubmitted(false);
        if (uid && roleId) {
            dispatch(fetchFromCCList({ uid, rid: roleId }));
            dispatch(fetchRecievedCCList({ uid, rid: roleId }));
            dispatch(fetchCategories());
        }
    };

    const handleSubmitTransfer = async () => {
        if (!fromCC) { toast.warning('Select Transfer From Cost Centre'); return; }
        if (!toCC) { toast.warning('Select Transfer To Cost Centre'); return; }
        if (!transferDate) { toast.warning('Select Date'); return; }
        if (!days) { toast.warning('Select No of Days'); return; }
        if (!commonRemarks.trim()) { toast.warning('Enter Common Remarks'); return; }
        if (cartItems.length === 0) { toast.error('Please Add Items to Transfer'); return; }

        const uncheckedCount = cartItems.filter((r) => !cartChecked[r.OItemcode]).length;
        if (uncheckedCount > 0) { toast.error('Please Verify Transfer Item Codes'); return; }
        const missingRemarkCount = cartItems.filter((r) => !(cartRemarks[r.OItemcode] || '').trim()).length;
        if (missingRemarkCount > 0) { toast.error('Enter Item Remarks'); return; }

        const codes = cartItems.map((r) => r.OItemcode);
        if (new Set(codes).size !== codes.length) { toast.error('Duplicate Transfer Item Code'); return; }

        const payload = {
            SItemcodes: cartItems.map((r) => r.OItemcode).join(',') + ',',
            SItemqtys: cartItems.map((r) => r.OItemqty).join(',') + ',',
            SItemamts: cartItems.map((r) => r.OItemamt).join(',') + ',',
            SCCCode: fromCC,
            SRECCCCode: toCC,
            SDate: formatDateForSP(transferDate),
            Days: parseInt(days, 10),
            SRemarks: commonRemarks.trim(),
            SIndRemarks: cartItems.map((r) => (cartRemarks[r.OItemcode] || '').trim()).join(',') + ',',
            RoleID: roleId,
            Createdby: currentUser,
        };

        try {
            const result = await dispatch(saveTransfer(payload)).unwrap();
            const [status, refNo] = (result || '').split(',');
            if (status === 'Submited') {
                toast.success(`The Transfer No Is:- ${refNo} Generated Successfully`);
                setSubmitted(true);
            } else {
                toast.error(result || 'Failed to submit transfer');
            }
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Items Transferred');
        } finally {
            dispatch(clearSubmitResult());
        }
    };

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Repeat className="w-5 h-5 text-indigo-600" /> New Stock Transfer
                    </h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Transfer stock already at one Cost Centre to another</p>
                </div>
            </div>

            <SectionCard icon={Building2} title="Transfer Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label required>Transfer From Cost Centre</Label>
                        <select className={inputCls} value={fromCC} onChange={(e) => handleFromCCChange(e.target.value)} disabled={loading.fromCC}>
                            <option value="">-Please Select-</option>
                            {fromCCList.map((c) => <option key={c.CCID} value={c.CCID}>{c.CCVAL}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label required>Transfer To Cost Centre</Label>
                        <select className={inputCls} value={toCC} onChange={(e) => setToCC(e.target.value)} disabled={loading.recCC}>
                            <option value="">-Please Select-</option>
                            {recCCList.map((c) => <option key={c.CCID} value={c.CCID}>{c.CCVAL}</option>)}
                        </select>
                    </div>
                </div>
            </SectionCard>

            <SectionCard icon={Search} title="Search Item" accent="amber">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-3">
                        <Label required>Item Category</Label>
                        <select className={inputCls} value={category} onChange={(e) => handleCategoryChange(e.target.value)} disabled={!fromCC}>
                            <option value="">-Please Select-</option>
                            {categoryList.map((c) => <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-3">
                        <Label required>Search By</Label>
                        <select className={inputCls} value={selectionType} onChange={(e) => handleSelectionTypeChange(e.target.value)} disabled={!category}>
                            <option value="">-Please Select-</option>
                            {SELECTION_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-4 relative">
                        <Label required>Search Item</Label>
                        <input
                            type="text"
                            className={inputCls}
                            style={{ textTransform: 'uppercase' }}
                            value={searchText}
                            disabled={!selectionType}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                            placeholder="Type item code or name…"
                        />
                        {showSuggestions && (
                            <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                {suggestionsLoading ? (
                                    <div className="px-3 py-2 text-gray-400 flex items-center gap-2 text-sm"><SpinnerIcon />Searching…</div>
                                ) : suggestions.length === 0 ? (
                                    <div className="px-3 py-2 text-gray-400 text-sm">No matches</div>
                                ) : (
                                    suggestions.map((v, i) => (
                                        <div key={i} className="px-3 py-1.5 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 cursor-pointer" onMouseDown={() => selectSuggestion(v.SearchItem)}>
                                            {v.SearchItem}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-2 flex gap-2">
                        <button onClick={handleAddItem} disabled={tempLoading} className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-1.5">
                            {tempLoading ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                        </button>
                        <button onClick={handleClearSearch} className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold">Clear</button>
                    </div>
                </div>
            </SectionCard>

            {tempItems.length > 0 && (
                <SectionCard icon={Plus} title="Add Items to Transfer" accent="amber">
                    <div className="overflow-x-auto rounded-lg border border-amber-200 dark:border-amber-700">
                        <table className="min-w-full divide-y divide-amber-200 dark:divide-amber-700 text-xs">
                            <thead className="bg-amber-100/60 dark:bg-amber-900/30">
                                <tr>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">Received Date</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Code</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">MRR No</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">PO No</th>
                                    <th className="px-2 py-1.5 text-right font-semibold uppercase">Quantity</th>
                                    <th className="px-2 py-1.5 text-right font-semibold uppercase">Assigned Qty</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                                {tempItems.map((row) => (
                                    <tr key={row.TempId}>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Tempdate}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.TempItemcode}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Tempmrr}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Temppono}</td>
                                        <td className="px-2 py-1 text-right">{num(row.TempQuantity).toFixed(4)}</td>
                                        <td className="px-2 py-1 text-right">
                                            <input
                                                type="text"
                                                className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs text-right bg-white dark:bg-gray-700"
                                                value={tempQtyEdits[row.TempId] ?? '0.00'}
                                                onChange={(e) => handleTempQtyChange(row, e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-amber-100/60 dark:bg-amber-900/30 font-bold">
                                    <td className="px-2 py-1" colSpan={4}></td>
                                    <td></td>
                                    <td className="px-2 py-1 text-right">{tempTotals.assigned.toFixed(4)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div className="text-right">
                        <button onClick={handleAddToTransfer} disabled={loading.saveTemp} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60">
                            {loading.saveTemp ? 'Adding…' : 'Add To Transfer'}
                        </button>
                    </div>
                </SectionCard>
            )}

            {fromCC && (
                <SectionCard icon={Building2} title="Item Code Details">
                    {cartLoading ? (
                        <div className="flex items-center justify-center gap-3 py-8"><SpinnerIcon /><span className="text-sm text-gray-500">Loading cart…</span></div>
                    ) : cartItems.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-6">No items added to this transfer yet.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                                <table className="min-w-full divide-y divide-indigo-200 dark:divide-indigo-700 text-xs">
                                    <thead className="bg-indigo-100/60 dark:bg-indigo-900/30">
                                        <tr>
                                            <th className="px-2 py-1.5 text-center font-semibold uppercase">Chk</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Code</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Name</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Specification</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Units</th>
                                            <th className="px-2 py-1.5 text-right font-semibold uppercase">Quantity</th>
                                            <th className="px-2 py-1.5 text-right font-semibold uppercase">Amount</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Remarks</th>
                                            <th className="px-2 py-1.5 text-center font-semibold uppercase">Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-indigo-100 dark:divide-indigo-800">
                                        {cartItems.map((row) => (
                                            <tr key={row.OItemcode}>
                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!cartChecked[row.OItemcode]} onChange={(e) => handleCartCheck(row.OItemcode, e.target.checked)} /></td>
                                                <td className="px-2 py-1 whitespace-nowrap font-medium">{row.OItemcode}</td>
                                                <td className="px-2 py-1">{row.OItemname}</td>
                                                <td className="px-2 py-1">{row.OItemspecification}</td>
                                                <td className="px-2 py-1 whitespace-nowrap">{row.OUnits}</td>
                                                <td className="px-2 py-1 text-right">{num(row.OItemqty).toFixed(4)}</td>
                                                <td className="px-2 py-1 text-right">{num(row.OItemamt).toFixed(2)}</td>
                                                <td className="px-2 py-1">
                                                    <textarea rows={1} className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700"
                                                        value={cartRemarks[row.OItemcode] || ''} onChange={(e) => handleCartRemark(row.OItemcode, e.target.value)} />
                                                </td>
                                                <td className="px-2 py-1 text-center">
                                                    <button onClick={() => handleDeleteCartItem(row)} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[11px] flex items-center gap-1 mx-auto">
                                                        <Trash2 className="w-3 h-3" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                            <td colSpan={6}></td>
                                            <td className="px-2 py-1 text-right">{cartTotal.toFixed(2)}</td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                                <div>
                                    <Label required>Transfer Date</Label>
                                    <CustomDatePicker value={transferDate} onChange={(d) => setTransferDate(formatDateForSP(d))} format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                                </div>
                                <div>
                                    <Label required>No of Days</Label>
                                    <select className={inputCls} value={days} onChange={(e) => setDays(e.target.value)}>
                                        <option value="">-Please Select-</option>
                                        {DAYS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label required>Common Remarks</Label>
                                    <textarea rows={2} className={inputCls} value={commonRemarks} onChange={(e) => setCommonRemarks(e.target.value)} />
                                </div>
                            </div>

                            <div className="text-right space-x-2">
                                <button onClick={handleReset} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5">
                                    <RefreshCw className="w-4 h-4" /> Reset
                                </button>
                                <button onClick={handleSubmitTransfer} disabled={loading.submit || submitted} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                                    <Send className="w-4 h-4" /> {loading.submit ? 'Submitting…' : 'Submit'}
                                </button>
                            </div>
                        </>
                    )}
                </SectionCard>
            )}
        </div>
    );
};

export default NewStockTransfer;
